import React, { useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OUTLINED_INPUT_HEIGHT } from '@/constants/dimensions';
import { useLocalization } from '@/hooks/useLocalization';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  useGetAdvisorInvestorUserProfileQuery,
  useUpdateAdvisorInvestorUserProfileMutation,
  type UserProfile,
} from '@/store/usersApi';
import {
  useUserProfileForm,
  UserProfileFormFields,
} from '@/modules/userProfile/UserProfileForm';

export interface ViewUserProfileModalProps {
  open: boolean;
  onClose: () => void;
  investorId: number;
  investorName: string;
}

const ViewUserProfileModal: React.FC<ViewUserProfileModalProps> = ({
  open,
  onClose,
  investorId,
  investorName,
}) => {
  const { t } = useLocalization();
  const authLoaded = useAuthIsLoaded();

  const {
    data: profile,
    isFetching,
    refetch,
  } = useGetAdvisorInvestorUserProfileQuery(investorId, {
    skip: !open || !authLoaded,
  });
  const [updateProfile, { isLoading: isSaving }] =
    useUpdateAdvisorInvestorUserProfileMutation();

  const isBusy = !profile || isFetching;

  const defaultProfile: UserProfile = {
    UserProfileId: 0,
    UserId: 0,
    CanEditNameAndEmail: false,
    FirstName: '',
    LastName: '',
    EmailAddress: '',
    TaxId: null,
    SpouseTaxId: null,
    ApproximateNetWorth: null,
    PhoneNumber: null,
    EmploymentStatus: null,
    EmploymentStatusDisplay: null,
    BusinessTitle: null,
    Employer: null,
    Gender: null,
    MarriageStatus: null,
    MarriageStatusDisplay: null,
    Birthday: null,
    SpouseFirstName: null,
    SpouseLastName: null,
    SpouseBirthday: null,
    Dependents: [],
    Address: null,
    AddressLine1: null,
    City: null,
    State: null,
    Zip: null,
    InvestorId: null,
  };

  const handleSubmit = useCallback(
    async (payload: Partial<UserProfile>) => {
      await updateProfile({
        investorId,
        body: { ...payload, InvestorId: investorId },
      }).unwrap();
      await refetch();
      onClose();
    },
    [investorId, onClose, refetch, updateProfile],
  );

  const effectiveProfile = profile ?? defaultProfile;

  const formController = useUserProfileForm({
    profile: effectiveProfile,
    onSubmit: handleSubmit,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography variant='h6'>{investorName}</Typography>
        <IconButton
          onClick={onClose}
          aria-label={t('close', 'Close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent
        dividers={false}
        sx={{ pt: 0 }}
      >
        {isBusy ? (
          <UserProfileFormSkeleton />
        ) : (
          <UserProfileFormFields
            controller={formController}
            profile={effectiveProfile}
          />
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant='outlined'
        >
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          onClick={formController.handleSave}
          variant='contained'
          disabled={isSaving || !formController.canSubmit || isBusy}
        >
          {isSaving ? t('saving', 'Saving...') : t('save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewUserProfileModal;

const UserProfileFormSkeleton: React.FC = () => {
  return (
    <Stack
      spacing={4}
      sx={{ pt: 1 }}
    >
      <SectionSkeleton />
      <Divider />
      <SectionSkeleton />
      <Divider />
      <SectionSkeleton />
      <Divider />
      <SectionSkeleton />
      <Divider />
      <Stack spacing={2}>
        <Skeleton
          variant='rounded'
          height={OUTLINED_INPUT_HEIGHT}
        />
      </Stack>
    </Stack>
  );
};

const SectionSkeleton: React.FC = () => {
  return (
    <Stack spacing={2}>
      <Skeleton
        variant='text'
        width={200}
        height={28}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Skeleton
          variant='rounded'
          height={OUTLINED_INPUT_HEIGHT}
        />
        <Skeleton
          variant='rounded'
          height={OUTLINED_INPUT_HEIGHT}
        />
        <Skeleton
          variant='rounded'
          height={OUTLINED_INPUT_HEIGHT}
        />
        <Skeleton
          variant='rounded'
          height={OUTLINED_INPUT_HEIGHT}
        />
      </Box>
    </Stack>
  );
};
